import { Box, makeStyles, LabelValue, Typography } from '@dxb-web-ui-framework/design-system';
import React, { FC } from 'react';
import { TFunction } from 'react-i18next';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { getClientTimezone, stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { ConsolidatedTransactionDetails } from '../../../domain/Transaction/entities/ConsolidatedTransaction';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(5),
    },
  },
  boxContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    border: `2px solid ${theme.palette.background.default}`,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      backgroundColor: '#F0F0F0',
    },
  },
  labelValueWrapper: {
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0),
    },
  },
}));

export interface TransactionDetailsModificationsProps {
  t: TFunction;
  transactionDetails: Nullable<ConsolidatedTransactionDetails>;
  isLoading: boolean;
}

const TransactionDetailsModifications: FC<TransactionDetailsModificationsProps> = ({
  t,
  transactionDetails,
  isLoading,
}) => {
  const classes = useStyles();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const locale = t('global.settings.date.locale');
  const dateFormat = t('global.settings.date.format');
  const timezone = getClientTimezone();

  return (
    <Box className={classes.root}>
      <Typography variant={'subtitle2'} color={'textPrimary'}>
        {t('transactionDetailsContent.modifications')}
      </Typography>
      <Box p={2} className={classes.boxContainer}>
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('transactionDetailsModifications.insertedBy')}
            value={transactionDetails?.metadata ? transactionDetails.metadata.insertedBy : '-'}
            valueAreaLabel={t('transactionDetailsModifications.insertedBy')}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        {transactionDetails?.creationDate && (
          <Box className={classes.labelValueWrapper}>
            <LabelValue
              label={t('transactionDetailsModifications.insertedDate')}
              value={
                transactionDetails?.metadata
                  ? stringToDate({
                      date: transactionDetails.metadata.insertedDateTime,
                      formatExp: dateFormat,
                      localeName: locale,
                      tz: timezone,
                    })
                  : '-'
              }
              valueAreaLabel={t('transactionDetailsModifications.insertedDate')}
              labelColor='textPrimary'
              labelSize='small'
              valueSize={isMobile ? 'small' : 'medium'}
              valueColor='primary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </Box>
        )}
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('transactionDetailsModifications.updatedBy')}
            value={transactionDetails?.metadata ? transactionDetails.metadata.updatedBy : '-'}
            valueAreaLabel={t('transactionDetailsModifications.updatedBy')}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        {transactionDetails?.valueDate && (
          <Box className={classes.labelValueWrapper}>
            <LabelValue
              label={t('transactionDetailsModifications.updatedDate')}
              value={
                transactionDetails?.metadata
                  ? stringToDate({
                      date: transactionDetails.metadata.updatedDateTime,
                      formatExp: dateFormat,
                      localeName: locale,
                      tz: timezone,
                    })
                  : '-'
              }
              valueAreaLabel={t('transactionDetailsModifications.updatedDate')}
              labelColor='textPrimary'
              labelSize='small'
              valueSize={isMobile ? 'small' : 'medium'}
              valueColor='primary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { TransactionDetailsModifications };
